/* component-variant-picker.js — <variant-picker>
   THE critical PDP component. Resolves the selected variant by EXACT option
   matching (never "contains"), then broadcasts kk:variant:changed so price,
   buy-buttons, sticky-atc, gallery, shipping, scarcity all update.

   Owns a MINIMAL variant map embedded by snippets/variant-picker.liquid:
     <script type="application/json" id="variants-<sectionid>" data-variant-json>
       [{ id, price, compare_at_price, available, options:[...],
          featured_media:{id,src,w,h} }]
     </script>

   Controls (any mix, one group per option position):
     - buttons:  <input type="radio" name="option-<pos>" value="…">
     - dropdown: <select data-option-index="<pos>">
     - swatch:   same radios, styled as swatches
   Each control carries data-option-position (1-based).

   On change:
     1. read the chosen value for every option position
     2. find the variant whose options array EXACTLY equals the chosen tuple
     3. update hidden form input [name="id"] (data-variant-id-input)
     4. update ?variant= in the URL (when data-update-url)
     5. disable option values that can't combine with current selection
     6. publish kk:variant:changed { variant, product:{id,title}, sectionId }

   On load: publishes GA4 view_item via KK.track. */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.section;
    this.updateUrl = this.dataset.updateUrl !== 'false';
    this.productId = this.dataset.productId;
    this.productTitle = this.dataset.productTitle || '';
    this.optionCount = parseInt(this.dataset.optionCount || '1', 10);

    const json = this.querySelector('[data-variant-json]');
    try { this.variants = json ? JSON.parse(json.textContent) : []; }
    catch (e) { this.variants = []; }

    this.idInput = document.querySelector('[data-variant-id-input][form="' + this._formId() + '"]')
      || this.querySelector('[data-variant-id-input]')
      || document.querySelector('#' + this._formId() + ' [name="id"]');

    this._controls = Array.from(this.querySelectorAll('[data-option-position]'));
    this._onChange = (e) => this.onSelectionChange(e);
    this._controls.forEach((el) => el.addEventListener('change', this._onChange));

    // Resolve + broadcast initial variant, and fire GA4 view_item once.
    const initial = this.currentVariant();
    this._updateDisabledStates(initial);
    if (initial) this._emitViewItem(initial);

    this._revealCheckedChips();
  }

  // Horizontally scrollable chip rows (topper slide chips): the preselected value can
  // sit past the fold — center it without scrolling the page itself.
  _revealCheckedChips() {
    this.querySelectorAll('.kk-vp__group').forEach((group) => {
      if (group.scrollWidth <= group.clientWidth + 1) return;
      const checked = group.querySelector('.kk-vp__radio:checked');
      const label = checked && group.querySelector('label[for="' + checked.id + '"]');
      const target = label || (checked && checked.closest('.kk-vp__value'));
      if (!target) return;
      group.scrollLeft = Math.max(0, target.offsetLeft - (group.clientWidth - target.offsetWidth) / 2);
    });
  }

  disconnectedCallback() {
    if (this._controls) this._controls.forEach((el) => el.removeEventListener('change', this._onChange));
  }

  _formId() { return 'product-form-' + this.sectionId; }

  // Chosen value for each option position (1-based), in order.
  selectedOptions() {
    const values = [];
    for (let pos = 1; pos <= this.optionCount; pos++) {
      const checked = this.querySelector('input[type="radio"][data-option-position="' + pos + '"]:checked');
      if (checked) { values.push(checked.value); continue; }
      const select = this.querySelector('select[data-option-position="' + pos + '"]');
      if (select) { values.push(select.value); continue; }
      values.push(null);
    }
    return values;
  }

  // EXACT-equality variant resolution. No "contains".
  currentVariant() {
    const chosen = this.selectedOptions();
    return this.variants.find(function (v) {
      if (!v.options || v.options.length !== chosen.length) return false;
      for (let i = 0; i < chosen.length; i++) {
        if (chosen[i] == null) return false;
        if (v.options[i] !== chosen[i]) return false; // strict string compare
      }
      return true;
    }) || null;
  }

  onSelectionChange(e) {
    let variant = this.currentVariant();

    /* Live-parity auto-snap: on products with DISJOINT option combos (e.g.
       komfortkissen-1 — each Bezugart ships in different Farben, 19 of 57
       combos exist), picking a value whose combination has no variant must
       NOT dead-end. Keep the value the customer just chose and snap the
       OTHER options to the first (preferably available) variant carrying it.
       Without this the UI desyncs: radios show one thing, the hidden form id
       still holds the previous variant, and ATC adds the WRONG product. */
    if (!variant && e && e.target && e.target.hasAttribute('data-option-position')) {
      this._snapToValidCombo(e.target);
      variant = this.currentVariant();
    }

    this._updateDisabledStates(variant);

    if (!variant) {
      // Still invalid (no variant carries the chosen value at all) — signal
      // unavailable, keep id empty.
      this._publish(null);
      return;
    }

    if (this.idInput) {
      this.idInput.value = variant.id;
      this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (this.updateUrl && window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
    this._publish(variant);
  }

  /* Given the control the customer just changed, find a variant that carries
     its value (available first) and align all OTHER option controls to that
     variant's values. Programmatic .checked / .value writes do NOT fire
     'change', so this cannot recurse. Returns the candidate or null. */
  _snapToValidCombo(control) {
    const pos = parseInt(control.getAttribute('data-option-position'), 10);
    if (!pos) return null;
    const idx = pos - 1;
    const value = control.value;
    const candidate =
      this.variants.find((v) => v.options && v.options[idx] === value && v.available) ||
      this.variants.find((v) => v.options && v.options[idx] === value);
    if (!candidate) return null;
    for (let p = 1; p <= this.optionCount; p++) {
      if (p === pos) continue;
      const want = candidate.options[p - 1];
      this.querySelectorAll('input[type="radio"][data-option-position="' + p + '"]').forEach((r) => {
        r.checked = r.value === want;
      });
      const select = this.querySelector('select[data-option-position="' + p + '"]');
      if (select && select.value !== want) select.value = want;
    }
    return candidate;
  }

  _publish(variant) {
    if (window.KK && window.KK.pubsub) {
      window.KK.pubsub.publish('kk:variant:changed', {
        variant: variant,
        product: { id: this.productId, title: this.productTitle },
        sectionId: this.sectionId
      });
    }
  }

  /* Live parity (komfortkissen-1): each Farbe swatch is HIDDEN — not just struck —
     when it forms no existing variant with the chosen Bezugart. Generic cascading
     filter: a value at option position P is hidden unless some variant carries it
     together with the currently-selected values at every EARLIER position (P-1…1).
     Consequences that match live exactly:
       - Option 1 (Bezugart) has no earlier position → never filtered → all values
         always shown (live keeps all 3 Bezugart buttons visible, un-struck, enabled).
       - Option 2 (Farbe) is filtered by the chosen Bezugart → 9 / 5 / 5 visible.
     A combo that DOES exist but is out of stock (e.g. Aroma/Flamingo under Baumwolle)
     stays VISIBLE and is only dimmed via `is-unavailable` (opacity), never hidden.
     Only toggles classes / hidden props — fires no change events, so no recursion. */
  _updateDisabledStates(current) {
    const chosen = this.selectedOptions();
    const variants = this.variants;

    // Does any variant carry `value` at position idx together with the selections
    // at all EARLIER positions? (Later positions are ignored — cascading filter.)
    function comboExists(idx, value) {
      return variants.some(function (v) {
        if (!v.options || v.options[idx] !== value) return false;
        for (let q = 0; q < idx; q++) {
          if (chosen[q] != null && v.options[q] !== chosen[q]) return false;
        }
        return true;
      });
    }
    // Availability of the FULL chosen tuple with position idx set to `value`.
    function availOf(idx, value) {
      const trial = chosen.slice();
      trial[idx] = value;
      const match = variants.find(function (v) {
        if (!v.options) return false;
        for (let i = 0; i < trial.length; i++) {
          if (trial[i] == null) continue; // ignore unfilled positions
          if (v.options[i] !== trial[i]) return false;
        }
        return true;
      });
      return match ? match.available : false;
    }

    const self = this;
    for (let pos = 1; pos <= this.optionCount; pos++) {
      const idx = pos - 1;
      // Availability rule per position (owner: "the material looks like OOS"):
      //  - Position 1 (Bezugart/Größe): a value is unavailable ONLY when no sellable
      //    variant carries it AT ALL. Cross-checking it against the currently selected
      //    later options struck Bambus/Mikroplüsch whenever the chosen colour didn't
      //    exist in them — but clicking auto-snaps to a valid colour, and live never
      //    strikes these. Single-option products (topper sizes) are unaffected:
      //    options[0] IS the variant, so genuinely sold-out sizes stay struck.
      //  - Later positions keep the full-tuple check (colour struck when the chosen
      //    Bezugart+colour variant is out of stock).
      function valueAvailable(idx2, value) {
        if (idx2 === 0) {
          return variants.some(function (v) { return v.options && v.options[0] === value && v.available; });
        }
        return availOf(idx2, value);
      }
      const radios = this.querySelectorAll('input[type="radio"][data-option-position="' + pos + '"]');
      radios.forEach(function (radio) {
        const exists = comboExists(idx, radio.value);
        const available = exists && valueAvailable(idx, radio.value);
        // Hide non-existent combos; dim (strike) existing-but-unavailable ones.
        radio.classList.toggle('is-combo-hidden', !exists);
        radio.classList.toggle('is-unavailable', exists && !available);
        const label = self.querySelector('label[for="' + radio.id + '"]');
        if (label) {
          label.classList.toggle('is-combo-hidden', !exists);
          label.classList.toggle('is-unavailable', exists && !available);
        }
      });
      const select = this.querySelector('select[data-option-position="' + pos + '"]');
      if (select) {
        Array.from(select.options).forEach(function (opt) {
          const exists = comboExists(idx, opt.value);
          const available = exists && valueAvailable(idx, opt.value);
          opt.hidden = !exists;
          opt.disabled = !exists || !available;
        });
      }
    }
  }

  _emitViewItem(variant) {
    if (!(window.KK && window.KK.track)) return;
    let currency = 'EUR';
    try { if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) currency = window.Shopify.currency.active; } catch (e) {}
    window.KK.track('view_item', {
      ecommerce: {
        currency: currency,
        value: (Number(variant.price) || 0) / 100,
        items: [{
          item_id: String(variant.sku || variant.id),
          item_name: this.productTitle,
          price: (Number(variant.price) || 0) / 100,
          quantity: 1,
          item_variant: (variant.options || []).join(' / ')
        }]
      }
    });
  }
}

if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);
